import { useState } from "react";
import { createTemplateAsync, updateTemplateAsync, assignTemplateActivitiesAsync, enableTemplateAsync, disableTemplateAsync, changeTemplateOriginScopeAsync } from "@features/report/template/services/reportTemplateSvc";
import { CreateTemplateRequest, UpdateTemplateRequest, AssignTemplateActivitiesRequest } from "@features/report/template/types/reportTemplateRequests";
import { OriginScope } from "@features/report/template/types/reportTemplate";

/**
 * Hook exposing report template actions and a local busy flag.
 *
 * Actions: doTemplateCreate, doTemplateUpdate, doTemplateAssignActivity.
 * Each sets busy = true before calling the corresponding async service and sets busy = false after it resolves.
 * Errors from the service calls propagate to the caller (busy may remain true on error; consider try/finally when calling).
 */
export function useReportTemplateActions() {
    const [busy, setBusy] = useState(false);

    /**
     * Create a report template for the specified nation and department context.
     *
     * While the creation request is in flight, this function sets the local busy
     * state to true and resets it to false once the request completes.
     * Any errors thrown by `createTemplateAsync` are not handled here and will
     * propagate to the caller.
     *
     * @param nationId - The numeric ID of the nation context for the template.
     * @param departmentId - The numeric ID of the department for the template.
     * @param data - The template payload to create (CreateTemplateRequest).
     * @returns A Promise that resolves to the response returned by `createTemplateAsync`.
     */
    const doTemplateCreate = async (nationId: number, departmentId: number, data: CreateTemplateRequest) => {
        setBusy(true);

        const response = await createTemplateAsync(nationId, departmentId, data);

        setBusy(false);
        return response;
    };

    /**
     * Updates a report template by calling the asynchronous updateTemplateAsync helper
     * while toggling the local busy state.
     *
     * Behavior:
     * - Calls setBusy(true) before invoking the update.
     * - Awaits the result of updateTemplateAsync and returns it.
     * - Calls setBusy(false) after a successful update.
     *
     * Note: In the current implementation, if updateTemplateAsync throws, setBusy(false)
     * is not executed and the busy state will remain true. Callers should handle errors
     * appropriately or the implementation should be adjusted to guarantee cleanup (e.g. try/finally).
     *
     * @param nationId - The nation identifier context for the template.
     * @param departmentId - The department identifier associated with the template.
     * @param templateId - The identifier of the template to update.
     * @param data - The payload to apply to the template (UpdateTemplateRequest).
     * @returns A Promise that resolves to the response returned by updateTemplateAsync.
     * @throws Propagates any error thrown by updateTemplateAsync.
     */
    const doTemplateUpdate = async (nationId: number, departmentId: number, templateId: number, data: UpdateTemplateRequest) => {
        setBusy(true);

        const response = await updateTemplateAsync(nationId, departmentId, templateId, data);

        setBusy(false);
        return response;
    };

    /**
     * Assigns activities from a template for a given nation, department and template identifiers.
     *
     * Sets a "busy" state to true before calling the API helper assignTemplateActivitiesAsync and sets it back to false
     * after the call completes, then returns the API response.
     *
     * Note: errors thrown by assignTemplateActivitiesAsync are not caught here and will propagate to the caller.
     * Because there is no try/finally, the busy state may remain true if an error is thrown.
     *
     * @async
     * @param nationId - Numeric identifier for the nation entity owning the assignment.
     * @param departmentId - Numeric identifier of the department where the template will be assigned.
     * @param templateId - Numeric identifier of the template whose activities are being assigned.
     * @param data - Payload conforming to AssignTemplateActivitiesRequest describing which activities to assign and any options.
     * @returns A promise that resolves with the response from assignTemplateActivitiesAsync (i.e. the API response).
     * @throws Any error thrown by assignTemplateActivitiesAsync; caller is responsible for handling errors.
     */
    const doTemplateAssignActivity = async (nationId: number, departmentId: number, templateId: number, data: AssignTemplateActivitiesRequest) => {
        setBusy(true);

        const response = await assignTemplateActivitiesAsync(nationId, departmentId, templateId, data);

        setBusy(false);
        return response;
    };

    const doTemplateDisable = async (nationId: number, departmentId: number, templateId: number) => {
        setBusy(true);
        const response = await disableTemplateAsync(nationId, departmentId, templateId);
        setBusy(false);
        return response;
    };

    const doTemplateEnable = async (nationId: number, departmentId: number, templateId: number) => {
        setBusy(true);
        const response = await enableTemplateAsync(nationId, departmentId, templateId);
        setBusy(false);
        return response;
    };

    const doTemplateChangeOriginScope = async (nationId: number, departmentId: number, templateId: number, originScope: OriginScope) => {
        setBusy(true);
        const response = await changeTemplateOriginScopeAsync(nationId, departmentId, templateId, originScope);
        setBusy(false);
        return response;
    };

    return { busy, doTemplateCreate, doTemplateUpdate, doTemplateAssignActivity, doTemplateDisable, doTemplateEnable, doTemplateChangeOriginScope };
}